import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import iconMoney from "../../assets/fi_10349139.png";
import iconCLose from "../../assets/Group (3).png";
import iconCheck from "../../assets/IconCheck.png";
import iconLoc from "../../assets/Vector (33).png";
import FitCultural from "../../ScreensCompanies/TalentSearch/components/FitCultural";
import Knowledge from "../../ScreensCompanies/TalentSearch/components/Knowledge";
import Skills from "../../ScreensCompanies/TalentSearch/components/Skills";
import publicProfileService from "../../services/publicProfileService";
import LoadingIcon from "../../components/LoadingIcon";
import TalentAvatar from "../../components/TalentAvatar/TalentAvatar";
import TagLabel from "../../components/TagLabel/TagLabel";
import PublicVideoPlayer from "./PublicVideoPlayer";
import iconSelfieLetter from "../../assets/poses/astronout-standing-ringlight-grayscale.svg";
import "../../ScreensCompanies/TalentSearch/DetailsTalent.css";
import "../../ScreensCompanies/TalentSearch/components/SelfieLetter.css";
import "./PublicTalentProfile.css";

const PublicTalentProfile = () => {
  const { slug } = useParams();
  const [talent, setTalent] = useState(null);
  const [activeTab, setActiveTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const navigate = useNavigate();

  // TODO: Remover hard coded quando API retornar salaryNegotiable
  const salaryNegotiable = talent?.salaryNegotiable !== undefined ? talent.salaryNegotiable : false;
  const icon = salaryNegotiable ? iconCheck : iconCLose;
  const text = salaryNegotiable ? "Valor negociável" : "Mínimo não negociável";
  const iconSize = salaryNegotiable
    ? { width: "12px", height: "8.25px" }
    : { width: "11.29px", height: "15.06px" };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const fetchPublicTalent = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verificar se há token na URL
      const urlToken = publicProfileService.getTokenFromUrl(window.location.href);

      if (!urlToken) {
        setError("É necessário um token válido para acessar este perfil. Por favor, solicite um novo link.");
        return;
      }

      // Buscar perfil usando token da URL
      const profileData = await publicProfileService.getProfile(slug, urlToken);

      const talentData = {
        id: profileData?.data?.id,
        ...profileData?.data?.attributes,
      };

      setTalent(talentData);

      // Definir aba inicial baseado no conteúdo disponível
      // Só abre a aba selfie se for uma URL válida (string)
      if (talentData?.selfieLetter && typeof talentData.selfieLetter === 'string') {
        setActiveTab("selfie");
      } else if (talentData?.knowledgeSkills && talentData.knowledgeSkills.length > 0) {
        setActiveTab("conhecimentos");
      } else {
        setActiveTab("habilidades");
      }
    } catch (error) {
      setError(error.message || "Erro ao carregar o perfil. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicTalent();
  }, [slug]);

  const calculateAge = (birthdate) => {
    if (!birthdate) return '';
    const birthDate = new Date(birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return `${age} anos`;
  };

  const calculateTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expiration = new Date(expiresAt);
    const diff = expiration - now;
    
    if (diff <= 0) {
      return { expired: true };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return {
      expired: false,
      hours,
      minutes,
      seconds
    };
  };

  // Timer que atualiza a cada segundo
  useEffect(() => {
    if (!talent?.expiresAt) return;

    const updateTimer = () => {
      const remaining = calculateTimeRemaining(talent.expiresAt);
      setTimeRemaining(remaining);
    };

    // Atualizar imediatamente
    updateTimer();

    // Atualizar a cada segundo
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [talent?.expiresAt]);

  const labelWorkModel = {
    "in_person": "Presencial",
    "remote": "Home Office",
    "hybrid": "Híbrido"
  };

  const labelContractType = {
    "clt": "CLT",
    "pj": "PJ",
    "both": "CLT ou PJ"
  };

  const labelSeniorityLevel = {
    "assistant": "Auxiliar",
    "associate": "Assistente",
    "junior_analyst": "Analista Júnior",
    "analyst": "Analista Pleno",
    "senior_analyst": "Analista Sênior",
    "coordinator": "Coordenador",
    "manager": "Gerente",
    "director": "Diretor",
    "c_level": "C-Level"
  };

  if (loading) {
    return (
      <div className="public-profile-container">
        <div className="loading-container">
          <LoadingIcon />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-profile-container">
        <div className="error-container">
          <h2>Ops!</h2>
          <p>{error}</p>
          <button
            className="back-button"
            onClick={() => navigate('/')}
          >
            Voltar para a página inicial
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container">
      <div className="public-profile-logo-header">
        <img src="/images/ecom-lovers-logo.png" alt="Ecom Lovers" className="public-logo" />
        <button
          onClick={() => navigate('/companies')}
          style={{
            background: '#EC5569',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '80px',
            fontFamily: 'IBM Plex Sans',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.background = '#d94458'}
          onMouseOut={(e) => e.target.style.background = '#EC5569'}
        >
          Acessar eComLovers
        </button>
      </div>

      <div className="public-profile-content">
        <div className="public-profile-card">
          <div className="containerBoxProfile">
          <div className="containerInfoProfile">
            <div className="avatarProfile">
              <TalentAvatar
                talent={talent}
                width="124px"
                height="124px"
                background="#EC5569"
                permitLightbox={true}
              />
            </div>

            <div className="containerTitleProfile">
              <div className="titleProfile">{talent?.name}</div>
              <div className="subTitleProfile">
                {talent?.city && talent?.state && (
                  <>
                    <img
                      src={iconLoc}
                      alt=" "
                      style={{ width: "12.38px", height: "15.75px" }}
                    />
                    <div className="textState">
                      {`${talent.city} - ${talent.state}`}
                    </div>
                  </>
                )}
                {talent?.age && (
                  <>
                    {talent?.city && talent?.state && <div className="separator">-</div>}
                    <div className="textState">
                      {talent.age} anos
                    </div>
                  </>
                )}
              </div>
            </div>

            {talent?.bio && (
              <div style={{
                fontSize: '14px',
                color: '#000',
                lineHeight: '1.6',
                marginTop: '12px',
                fontFamily: 'IBM Plex Sans',
                fontWeight: 300
              }}>
                {talent.bio}
              </div>
            )}

            {(talent?.seniorityLevel || talent?.contractType || (talent?.workModel && talent?.workModel.length > 0)) && (
              <div className="cardInfoProfile">
                {talent?.seniorityLevel && (
                  <TagLabel text={labelSeniorityLevel[talent?.seniorityLevel]} />
                )}

                {talent?.contractType && (
                  <TagLabel text={labelContractType[talent?.contractType]} />
                )}

                {talent.workModel && talent.workModel.length > 0 && (
                  <TagLabel
                    text={talent.workModel.map((model) => labelWorkModel[model]).join(' ou ')}
                  />
                )}
              </div>
            )}

            {(talent?.linkedinUrl || talent?.phone) && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {talent?.linkedinUrl && (
                  <a
                    href={talent.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#0077B5',
                      textDecoration: 'none',
                      fontFamily: 'IBM Plex Sans',
                      fontSize: '13px',
                      fontWeight: 400,
                      transition: 'color 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#005885'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#0077B5'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </a>
                )}

                {talent?.phone && (
                  <a
                    href={`https://wa.me/${talent.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#3BD1CA',
                      textDecoration: 'none',
                      fontFamily: 'IBM Plex Sans',
                      fontSize: '13px',
                      fontWeight: 400,
                      transition: 'color 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#0AC6BD'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#3BD1CA'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    WhatsApp
                  </a>
                )}
              </div>
            )}

            {talent?.salary && (
              <div className="cardInfoProfile">
                <div className="box1">
                  <div className="containerInfoBox">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                      }}
                    >
                      <img
                        src={iconMoney}
                        alt=" "
                        style={{ width: "16.13px", height: "16.13px" }}
                      />
                    </div>
                    <div className="text">{formatCurrency(talent?.salary)}</div>
                  </div>

                  <div className="containerInfoBox">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                      }}
                    >
                      <img src={icon} alt="" style={iconSize} />
                    </div>
                    <div className="text">{text}</div>
                  </div>
                </div>
              </div>
            )}

              {talent?.culturalFitResult && (talent?.culturalFitResult?.a + talent?.culturalFitResult?.i + talent?.culturalFitResult?.c + talent?.culturalFitResult?.e) > 0 && (
              <div>
                <FitCultural culturalFitResult={talent?.culturalFitResult} />
              </div>
            )}

            {timeRemaining && (
              <div className="token-expiration-timer">
                {timeRemaining.expired ? (
                  <div className="timer-expired">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>Link expirado</span>
                  </div>
                ) : (
                  <div className="timer-active">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>
                      Link expira em: {timeRemaining.hours.toString().padStart(2, '0')}:{timeRemaining.minutes.toString().padStart(2, '0')}:{timeRemaining.seconds.toString().padStart(2, '0')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="tabs-container">
            <div className="tabs-header">
              <button
                className={`tab-button ${
                  activeTab === "selfie" ? "active" : ""
                }`}
                onClick={() => handleTabClick("selfie")}
              >
                Selfie Letter
              </button>
              <button
                className={`tab-button ${
                  activeTab === "conhecimentos" ? "active" : ""
                }`}
                onClick={() => handleTabClick("conhecimentos")}
              >
                Conhecimentos
              </button>
              <button
                className={`tab-button ${
                  activeTab === "habilidades" ? "active" : ""
                }`}
                onClick={() => handleTabClick("habilidades")}
              >
                Habilidades
              </button>
            </div>
            <div className="tab-content">
              {activeTab === "conhecimentos" && talent && (
                <Knowledge talents={talent} />
              )}
              {activeTab === "selfie" && talent && (
                <>
                  {talent?.selfieLetter && typeof talent.selfieLetter === 'object' && talent.selfieLetter.message ? (
                    <div className="boxNull">
                      <div className="titleText">Acesso Restrito</div>
                      <div className="subText">
                        {talent.selfieLetter.message}
                      </div>
                      <div className="boxSelfieLetter">
                        <img src={iconSelfieLetter} alt="Selfie Letter" className="iconSelfieLetter" />
                      </div>
                    </div>
                  ) : talent?.selfieLetter && typeof talent.selfieLetter === 'string' ? (
                    <div className="boxSelfieLetter">
                      <div className="box2">
                        <PublicVideoPlayer videoUrl={talent.selfieLetter} />
                      </div>
                    </div>
                  ) : (
                    <div className="boxNull">
                      <div className="titleText">Selfie Letter não enviado</div>
                      <div className="subText">
                        Esse Talento ainda não atualizou suas informações de Selfie Letter.
                        Confira novamente mais tarde.
                      </div>
                      <div className="boxSelfieLetter">
                        <img src={iconSelfieLetter} alt="Selfie Letter" className="iconSelfieLetter" />
                      </div>
                    </div>
                  )}
                </>
              )}
              {activeTab === "habilidades" && talent && (
                <Skills talents={talent} />
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PublicTalentProfile;

